import { motion } from "framer-motion";
import { Sparkles, Lightbulb, AlertTriangle, ArrowRight, Check, Gauge } from "lucide-react";
import PowerComparison from "@/components/PowerComparison";
import { whatsappLink, buildVehicleWaMessage } from "@/lib/config";

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
        <path d="M16.004 0h-.008C7.174 0 .002 7.174.002 16c0 3.5 1.13 6.747 3.05 9.38L1.06 31.27l6.067-1.94A15.9 15.9 0 0 0 16.004 32C24.83 32 32 24.826 32 16S24.83 0 16.004 0zm9.31 22.594c-.386 1.09-1.92 1.995-3.144 2.26-.836.178-1.928.32-5.604-1.204-4.7-1.948-7.726-6.724-7.962-7.034-.226-.31-1.9-2.53-1.9-4.826 0-2.296 1.166-3.424 1.636-3.904.386-.396.844-.576 1.126-.576.366 0 .67.004.936.018.3.014.7-.114 1.094.834.404.972 1.374 3.36 1.494 3.604.122.244.204.53.04.84-.154.31-.232.502-.46.772-.232.27-.484.602-.69.808-.232.23-.474.48-.204.94.27.46 1.2 1.98 2.578 3.208 1.77 1.578 3.262 2.066 3.722 2.296.46.232.728.194.996-.116.27-.31 1.15-1.342 1.456-1.804.302-.46.61-.384 1.026-.23.42.154 2.658 1.252 3.116 1.48.46.232.764.346.876.538.116.192.116 1.108-.27 2.176z" />
    </svg>
);

export const ResultStep = ({ vehicle, result, onComplete }) => {
    const recommended =
        result.stages.find((s) => s.name === result.empfohlene_stage) || result.stages[0];

    const waMessage = buildVehicleWaMessage({ vehicle, stage: result.empfohlene_stage });

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-3xl">
            <div className="text-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-semibold text-white">KI Performance Analyse</span>
                </div>
                <h2 className="mt-4 font-heading text-2xl font-bold text-white sm:text-3xl">
                    {result.fahrzeug_zusammenfassung}
                </h2>
                <p className="mt-2 text-sm text-[#A1A1AA]">
                    Serienleistung: {result.serie_ps} PS · {result.serie_nm} Nm
                </p>
            </div>

            {/* Comparison */}
            <div className="mt-8">
                <PowerComparison
                    serie_ps={result.serie_ps}
                    serie_nm={result.serie_nm}
                    opt_ps={recommended.ps}
                    opt_nm={recommended.nm}
                    stageName={recommended.name}
                    beschleunigung={result.beschleunigung_verbesserung}
                    verbrauch={result.verbrauch_verbesserung}
                />
            </div>

            {/* Stages */}
            <div className="mt-8">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#A1A1AA]">
                    <Gauge className="h-4 w-4 text-primary" /> Mögliche Optimierungen
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                    {result.stages.map((s) => (
                        <div
                            key={s.name}
                            data-testid={`stage-${s.name.replace(/\s/g, "-").toLowerCase()}`}
                            className={`relative rounded-xl border p-5 ${
                                s.empfohlen
                                    ? "border-primary bg-primary/5"
                                    : "border-border bg-surface"
                            }`}
                        >
                            {s.empfohlen && (
                                <span className="absolute -top-2.5 left-4 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase text-white">
                                    Empfohlen
                                </span>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-base font-bold text-white">{s.name}</span>
                                <span className="text-sm font-semibold text-success">+{s.prozent_ps}%</span>
                            </div>
                            <div className="mt-2 flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-white">{s.ps}</span>
                                <span className="text-sm text-[#A1A1AA]">PS</span>
                                <span className="text-[#52525B]">·</span>
                                <span className="text-2xl font-bold text-white">{s.nm}</span>
                                <span className="text-sm text-[#A1A1AA]">Nm</span>
                            </div>
                            <p className="mt-3 text-xs leading-relaxed text-[#A1A1AA]">{s.beschreibung}</p>
                            <div className="mt-3 flex items-center gap-1.5 text-xs">
                                {s.hardware_noetig ? (
                                    <span className="flex items-center gap-1 text-[#F59E0B]">
                                        <AlertTriangle className="h-3.5 w-3.5" /> Hardware erforderlich
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-success">
                                        <Check className="h-3.5 w-3.5" /> Ohne Hardware möglich
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Empfehlung */}
            <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-5">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                    <Lightbulb className="h-4 w-4 text-primary" /> Empfehlung unseres KI Advisors
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#D4D4D8]">{result.empfehlung}</p>
            </div>

            {/* Hinweise */}
            {result.hinweise && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" />
                    <p className="text-sm leading-relaxed text-[#D4D4D8]">{result.hinweise}</p>
                </div>
            )}

            {/* CTA */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                    data-testid="result-request-offer-btn"
                    onClick={onComplete}
                    className="group inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-6 py-4 text-base font-bold text-white red-glow hover:bg-[#FF3B30] transition-colors"
                >
                    Angebot anfordern
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <a
                    data-testid="result-whatsapp-btn"
                    href={whatsappLink(waMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-4 text-base font-semibold text-white hover:bg-[#25D366]/20 transition-colors"
                >
                    <WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> Per WhatsApp
                </a>
            </div>
        </motion.div>
    );
};

export default ResultStep;
